import { useCallback, useEffect, useRef, useState } from "react";
import {
  turnstileSiteKey,
  TURNSTILE_SCRIPT_SRC,
} from "../utils/turnstileConfig";

/**
 * Lazily loads Cloudflare's Turnstile script exactly once and resolves with the
 * global `window.turnstile`. The script is injected into <head>; concurrent
 * callers share a single in-flight promise. SSR/test-safe — guards `window`.
 * @returns {Promise<object>} the global `window.turnstile`
 */
let scriptPromise = null;
const loadTurnstileScript = () => {
  if (typeof window === "undefined") {
    return Promise.reject(new Error("Turnstile is unavailable in this environment."));
  }
  if (window.turnstile) return Promise.resolve(window.turnstile);
  if (scriptPromise) return scriptPromise;

  scriptPromise = new Promise((resolve, reject) => {
    const onReady = () => {
      if (window.turnstile) resolve(window.turnstile);
      else reject(new Error("Turnstile script loaded without a global."));
    };
    const existing = document.querySelector(`script[src="${TURNSTILE_SCRIPT_SRC}"]`);
    if (existing) {
      if (window.turnstile) return onReady();
      existing.addEventListener("load", onReady);
      existing.addEventListener("error", () => reject(new Error("Failed to load Turnstile.")));
      return;
    }
    const script = document.createElement("script");
    script.src = TURNSTILE_SCRIPT_SRC;
    script.async = true;
    script.defer = true;
    script.onload = onReady;
    script.onerror = () => {
      scriptPromise = null;
      reject(new Error("Failed to load Turnstile."));
    };
    document.head.appendChild(script);
  });
  return scriptPromise;
};

/**
 * useTurnstile — renders a Cloudflare Turnstile widget (SPR-009, CON-002 §5A)
 * and exposes the verification token to include as `captchaToken` on the booking
 * POST. Loads the widget script lazily so it never blocks SSR/build/tests.
 *
 * Usage: attach `containerRef` to a <div>; read `token` (null until solved);
 * call `reset()` after a 422 captcha failure to clear + re-challenge.
 *
 * @returns {{
 *   containerRef: React.RefObject<HTMLDivElement>,
 *   token: string|null,
 *   ready: boolean,
 *   error: string|null,
 *   reset: () => void,
 * }}
 */
export default function useTurnstile() {
  const containerRef = useRef(null);
  const widgetIdRef = useRef(null);
  const turnstileRef = useRef(null);
  const [token, setToken] = useState(null);
  const [ready, setReady] = useState(false);
  const [error, setError] = useState(null);

  const reset = useCallback(() => {
    setToken(null);
    const ts = turnstileRef.current;
    if (ts && widgetIdRef.current != null) {
      try { ts.reset(widgetIdRef.current); } catch { /* ignore */ }
    }
  }, []);

  useEffect(() => {
    let cancelled = false;

    loadTurnstileScript()
      .then((ts) => {
        if (cancelled || !containerRef.current) return;
        turnstileRef.current = ts;
        widgetIdRef.current = ts.render(containerRef.current, {
          sitekey: turnstileSiteKey(),
          callback: (t) => setToken(t),
          "expired-callback": () => setToken(null),
          "error-callback": () => {
            setToken(null);
            setError("Verification failed to load. Please try again.");
          },
        });
        setReady(true);
      })
      .catch((e) => {
        if (!cancelled) setError(e?.message || "Verification failed to load.");
      });

    return () => {
      cancelled = true;
      const ts = turnstileRef.current;
      if (ts && widgetIdRef.current != null) {
        try { ts.remove(widgetIdRef.current); } catch { /* ignore */ }
      }
      widgetIdRef.current = null;
    };
  }, []);

  return { containerRef, token, ready, error, reset };
}
